import { useStoreLogger } from '@/logger.ts'
import { RmaMasterGetList as ApiRmaMasterGetList, RmaMasterGetByNumber as ApiRmaMasterGetByNumber } from '@/api/RMA.ts'
import { GetBeeNumberAuthenicatedUser } from '@/stores/SettingsStore.ts'

/**
 * Options for scoping the RmaMasterGetList query. Some payee accounts accumulate a very
 * large amount of historical RMAs, and fetching that entire history was causing
 * RmaMasterGetList to time out. RmaStatusId and CreatedTimeFromUtc let callers push
 * filtering down to the database instead of fetching everything and filtering client-side.
 */
export interface AOERMAMasterGetListOptions {
  /** RMA status keyword (e.g. 'OPEN', 'CLOSED') to filter by. Omit/undefined for no status filter. */
  rmaStatusId?: string
  /** ISO timestamp; only RMAs created on/after this time (CreatedTimeUtc) are returned. */
  createdTimeFromUtc?: string
  /** ISO timestamp; only RMAs created on/before this time (CreatedTimeUtc) are returned. */
  createdTimeToUtc?: string
}

/**
 * Retrieves a list of RMAs based on the provided filter options
 * @param options The UI filter options
 * @returns Promise with the array of RMA master view models
 */
export async function AOERMAMasterGetList(options?: AOERMAMasterGetListOptions) {
  const logger = useStoreLogger()

  // Get the current user's BeeBusinessGuid
  const beeNumberPayee = await GetBeeNumberAuthenicatedUser()

  // Always scope to the current payee. Additional AND conditions below narrow the query
  // further by status and/or creation date so we don't pull a user's entire RMA history.
  const andOperands: any[] = [
    {
      OperatorType: 'Or',
      Operands: [
        {
          Field: 'BeeNumberPayee',
          FilterType: 'Equal',
          Value: beeNumberPayee
        }
      ]
    }
  ]

  // Default: only fetch RMAs created on/after this timestamp. This is what keeps the
  // query bounded for accounts with a lot of historical RMAs.
  if (options?.createdTimeFromUtc) {
    andOperands.push({
      Field: 'CreatedTimeUtc',
      FilterType: 'GreaterThanOrEqual',
      Value: options.createdTimeFromUtc
    })
  }

  if (options?.createdTimeToUtc) {
    andOperands.push({
      Field: 'CreatedTimeUtc',
      FilterType: 'LessThanOrEqual',
      Value: options.createdTimeToUtc
    })
  }

  // Optional status filter. The backend resolves this keyword (e.g. 'OPEN') to the
  // corresponding CddCodeGuidRmaStatus for us.
  if (options?.rmaStatusId) {
    andOperands.push({
      Field: 'RmaStatusId',
      FilterType: 'Equal',
      Value: options.rmaStatusId
    })
  }

  const databaseFilter: any = {
    Where: {
      OperatorType: 'And',
      Operands: andOperands
    }
  }

  try {
    const result: any = await ApiRmaMasterGetList({
      DatabaseFilter: databaseFilter
    })

    return result.items
  } catch (error) {
    logger.error('Error in AOERMAMasterGetList', error)
    throw error
  }
}

/**
 * Retrieves a specific RMA by its number
 * @param rmaNumber The RMA number to retrieve
 * @returns Promise with the RMA master view model
 */
export async function AOERMAMasterGetByNumber(rmaNumber: string){
  const logger = useStoreLogger()
  logger.debug('AOERMAMasterGetByNumber', rmaNumber)
  
  try {
    return await ApiRmaMasterGetByNumber(rmaNumber)
  } catch (error) {
    logger.error('Error in AOERMAMasterGetByNumber', error)
    throw error
  }
}
